import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Link as LinkIcon, Check, Edit3, BookOpen, BookMarked, Sidebar, Minimize2, Terminal, Maximize2, MoreVertical } from "lucide-react";
import { useMobile } from "../../hooks/useMobile";
import { useEditLock } from "../../context/EditLockContext";
import { translateArticle } from "../../utils/translate";
import TranslationOverlay from "./TranslationOverlay";
import MarkdownRenderer from "../common/MarkdownRenderer";
import { addChecklistItem, createArticle, updateText, toSortedArray, applyReadingAction, getChildren, folderProgress, toggleTextDone } from "../../services/workspaceService";
import ArticleWidthControls, { useArticleWidth } from "../articles/ArticleWidthControls";
import deskGlass, { ACCENT } from "../../styles/deskGlass";
import "../articles/ArticleWidthControls.css";
import "../reading/reading.css";
import { BACKGROUNDS } from "../../config/backgrounds";
import { isTerminalPaste } from "../../utils/normalizePaste";
import { contentForReading } from "../../utils/contentForReading";
import QuickMoveBar from "../common/QuickMoveBar";
import ReadingActionBar from "../reading/ReadingActionBar";
import { useSwipeAction } from "../../hooks/useSwipeAction";
import { useWorkspace } from "../../context/WorkspaceContext";
import { runFeedbackFlow } from "../../utils/runFeedbackFlow";
import { emitFeedback } from "../../services/feedbackBus";
import { textReaderPath } from "../../utils/textRoutes";

const LANGS = [
  { code: "es", label: "ES" },
  { code: "en", label: "EN" },
  { code: "la", label: "LA" },
];

export default function TextReader({
  isOpen,
  onClose,
  onEdit,
  textId,
  title = "",
  content = "",
  img = "",
  slug = "",
  deskSlug = "",
  drawerSlug = "",
  notebookSlug = "",
  updatedAt = null,
  translations = {},
  onSaveTranslation,
  deskId,
  drawerId,
  notebookId,
  isRead = false,
  onToggleRead,
  inline = false,
  readingMode = false,
  onReadingAction,
  queueMeta = null,
  kind = null,
  onOpenChild = null,
}) {
  const overlayRef = useRef(null);
  const isMobile = useMobile();
  const { workspace, settings } = useWorkspace();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [lang, setLang] = useState("es");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [displayTitle, setDisplayTitle] = useState(title);
  const [displayContent, setDisplayContent] = useState(content);

  // Study Mode & Marginalia
  const [isStudyMode, setIsStudyMode] = useState(false);
  const [isTerminalMode, setIsTerminalMode] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [splitWidth, setSplitWidth] = useState(60); // Percentage for the text pane
  const [marginalia, setMarginalia] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [moveLocation, setMoveLocation] = useState(null);
  const [checklistDraft, setChecklistDraft] = useState("");
  const [addingChecklist, setAddingChecklist] = useState(false);
  const scrollPaneRef = useRef(null);
  const markedReadRef = useRef(false);

  const folderChildren = useMemo(() => {
    if (kind !== "folder" || !textId || !workspace?.desks) return [];
    const flat = [];
    for (const desk of workspace.desks) {
      for (const drawer of toSortedArray(desk.drawers)) {
        for (const notebook of toSortedArray(drawer.notebooks)) {
          for (const t of toSortedArray(notebook.texts)) {
            flat.push({
              ...t,
              deskId: desk.id,
              drawerId: drawer.id,
              notebookId: notebook.id,
            });
          }
        }
      }
    }
    return getChildren(flat, textId);
  }, [kind, textId, workspace]);

  const folderProg = folderProgress(folderChildren);

  const handleChildDone = async (child, e) => {
    e?.stopPropagation?.();
    if (locked) return;
    await toggleTextDone(child.deskId, child.drawerId, child.notebookId, child.id);
  };

  const handleAddChecklistItem = async (e) => {
    e?.preventDefault?.();
    const itemTitle = checklistDraft.trim();
    if (locked || !itemTitle || !deskId || !drawerId || !notebookId || !textId) return;
    setAddingChecklist(true);
    try {
      await addChecklistItem(deskId, drawerId, notebookId, textId, itemTitle);
      setChecklistDraft("");
    } catch (error) {
      console.error("[TextReader] checklist item failed:", error);
    } finally {
      setAddingChecklist(false);
    }
  };

  const handleReadingAction = useCallback(async (action) => {
    if (!deskId || !drawerId || !notebookId || !textId) {
      console.warn("[TextReader] reading action skipped — missing location ids", { deskId, drawerId, notebookId, textId, action });
      return;
    }
    const actionId = `reading-action:${textId}:${action}`;
    emitFeedback({ stage: "pressed", actionId, label: "Lectura" });
    try {
      await applyReadingAction(deskId, drawerId, notebookId, textId, action);
      emitFeedback({ stage: "db", actionId, label: "Lectura", message: "OK" });
      onReadingAction?.(action);
    } catch (err) {
      console.error("[TextReader] reading action failed:", err);
      emitFeedback({ stage: "error", actionId, label: "Lectura", message: err?.message || "No se pudo aplicar acción" });
    }
  }, [deskId, drawerId, notebookId, textId, onReadingAction]);

  const readerSwipe = useSwipeAction(
    {
      left: "later",
      right: "read",
      onAction: (action) => handleReadingAction(action),
    },
    { disabled: !readingMode }
  );

  const quickMoveLocation = useMemo(() => {
    if (moveLocation) return moveLocation;
    if (!workspace || !deskId || !drawerId || !notebookId || !textId) return null;
    const desk = workspace.desks?.find((d) => d.id === deskId);
    const drawer = desk && toSortedArray(desk.drawers).find((dr) => dr.id === drawerId);
    const notebook = drawer && toSortedArray(drawer.notebooks).find((n) => n.id === notebookId);
    if (!desk || !drawer || !notebook) return null;
    return {
      deskId,
      drawerId,
      notebookId,
      textId,
      page: title,
      path: `${desk.name} › ${drawer.name} › ${notebook.name}`,
      canQuickMove: true,
    };
  }, [moveLocation, workspace, deskId, drawerId, notebookId, textId, title]);

  useEffect(() => {
    setMoveLocation(null);
  }, [textId, deskId, drawerId, notebookId, isOpen]);

  // Article width controls with Liquid Glass
  const { width: articleWidth, setWidth: setArticleWidth, widthValue } = useArticleWidth();

  const handleCheckboxToggle = async (oldChecked, newChecked) => {
    if (locked) return;
    const lines = displayContent.split("\n");
    let foundIndex = -1;
    const wantDone = oldChecked;
    const markDone = newChecked;

    const newLines = lines.map((line, idx) => {
      if (foundIndex !== -1) return line;
      const trimmed = line.trim();
      const listRe = wantDone
        ? /^(-|\*)\s+\[x\]\s*/i
        : /^(-|\*)\s+\[\s\]\s*/i;
      const bareRe = wantDone ? /^\[x\]\s*/i : /^\[\s\]\s*/i;
      if (listRe.test(trimmed)) {
        foundIndex = idx;
        return markDone
          ? line.replace(/^(\s*(?:-|\*)\s+)\[\s\]/i, "$1[x]")
          : line.replace(/^(\s*(?:-|\*)\s+)\[x\]/i, "$1[ ]");
      }
      if (bareRe.test(trimmed)) {
        foundIndex = idx;
        return markDone
          ? line.replace(/^(\s*)\[\s\]/, "$1[x]")
          : line.replace(/^(\s*)\[x\]/i, "$1[ ]");
      }
      return line;
    });

    if (foundIndex !== -1) {
      const updatedContent = newLines.join("\n");
      setDisplayContent(updatedContent);
      if (textId && deskId && drawerId && notebookId) {
        try {
          await updateText(deskId, drawerId, notebookId, textId, { content: updatedContent });
        } catch (e) {
          console.error("Failed to sync checkbox state:", e);
        }
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLang("es");
      setDisplayTitle(title);
      const normalized = contentForReading(content);
      setDisplayContent(normalized);
      setLoading(false);
      setCopied(false);
      setMarginalia("");
      markedReadRef.current = false;
      const raw = (content || "").trim();
      setIsTerminalMode(/^```[\s\S]+```/m.test(raw) || isTerminalPaste(raw));
    }
  }, [isOpen, title, content]);

  useEffect(() => {
    if (!isOpen || !readingMode || isRead || markedReadRef.current) return undefined;
    const el = scrollPaneRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight <= clientHeight + 8) return;
      const ratio = (scrollTop + clientHeight) / scrollHeight;
      if (ratio >= 0.88 && !markedReadRef.current) {
        markedReadRef.current = true;
        handleReadingAction("read");
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [isOpen, readingMode, isRead, handleReadingAction]);

  // Resizing logic for split view
  const startResizing = (e) => {
    e.preventDefault();
    window.addEventListener("mousemove", handleResizing);
    window.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
  };

  const handleResizing = (e) => {
    const newWidth = (e.clientX / window.innerWidth) * 100;
    if (newWidth > 20 && newWidth < 80) {
      setSplitWidth(newWidth);
    }
  };

  const stopResizing = () => {
    window.removeEventListener("mousemove", handleResizing);
    window.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "default";
  };

  const handleLangChange = async (targetLang) => {
    if (targetLang === lang) return;
    if (targetLang === "es") {
      setLang("es");
      setDisplayTitle(title);
      setDisplayContent(content);
      return;
    }
    const cached = translations?.[targetLang];
    if (cached?.title && cached?.content) {
      setLang(targetLang);
      setDisplayTitle(cached.title);
      setDisplayContent(cached.content);
      return;
    }
    setLoading(true);
    try {
      const result = await translateArticle(title, content, "es", targetLang);
      setLang(targetLang);
      setDisplayTitle(result.title);
      setDisplayContent(result.content);
      if (!locked && onSaveTranslation && textId) onSaveTranslation(textId, targetLang, result);
    } catch (err) {
      console.error("Translation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMarginalia = async () => {
    if (locked) return;
    if (!marginalia.trim()) return;
    setIsSavingNote(true);
    try {
      const noteTitle = `Marginalia: ${title}`;
      const noteContent = `> Ref: ${title}\n\n${marginalia}`;
      // Save as a separate note in the same notebook
      await createArticle(noteTitle, noteContent, null, {
        deskName: "Buzón de Entrada",
        drawerName: "Marginalia",
        notebookName: "Estudio",
      });
      setMarginalia("");
      emitFeedback({
        stage: "db",
        actionId: "marginalia-save",
        label: "Marginalia",
        message: "Nota guardada en Marginalia.",
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleCopyUrl = () => {
    const path = textReaderPath({
      id: textId,
      slug,
      deskSlug,
      drawerSlug,
      notebookSlug,
    });
    const url = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isModal = !inline || isFocusMode;
  const compactChrome = isMobile && (isModal || inline);
  const readingLayout = readingMode && inline && !isFocusMode;

  // Apply article width to modal (desktop only)
  const modalWidthStyle = !isMobile && isModal ? { maxWidth: widthValue } : {};

  const handleOverlayClick = (e) => {
    if (isModal && e.target === overlayRef.current) onClose();
  };

  const formattedDate = updatedAt
    ? new Date(updatedAt).toLocaleString("es-AR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <>
      {/* Background image for article view (local assets) */}
      {isModal && !isMobile && (
        <div
          className="article-background"
          style={{
            backgroundImage: `url(${BACKGROUNDS.reader || BACKGROUNDS.default})`,
          }}
        />
      )}
      <div
        ref={overlayRef}
        style={{
          ...(isModal ? (isMobile ? styles.mobileOverlay : styles.overlay) : styles.inlineOverlay),
          ...(readingLayout ? styles.inlineReadingShell : {}),
        }}
        onClick={handleOverlayClick}
      >
        {/* Width presets: desktop only, top-right — never over action bar / catch-up */}
        {isModal && !isMobile && !isStudyMode && !readingMode && (
          <ArticleWidthControls
            currentWidth={articleWidth}
            onChange={setArticleWidth}
            position="top-right"
          />
        )}
        <div
          onClick={(e) => e.stopPropagation()}
          className={`article-container article-container--${articleWidth}`}
          style={{
          ...(isModal ? (isMobile ? styles.mobileModal : styles.modal) : styles.inlineModal),
          maxWidth: isStudyMode ? "100%" : (isModal ? (isMobile ? "100%" : modalWidthStyle.maxWidth || "800px") : "100%"),
          width: isStudyMode ? "100vw" : "100%",
          height: isStudyMode ? "100vh" : (isModal ? (isMobile ? "100%" : "90vh") : (readingLayout ? "100%" : "auto")),
          borderRadius: isStudyMode ? "0" : (isModal ? (isMobile ? "0" : "16px") : "0"),
          minHeight: !isModal ? (readingLayout ? 0 : "100%") : "auto",
          ...(compactChrome || readingLayout ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: isModal ? undefined : "12px 12px 0" } : {}),
        }}>
        {/* Header */}
        <div style={{ ...styles.header, ...(compactChrome ? { paddingBottom: "12px" } : {}) }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: compactChrome ? "8px" : "16px" }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: compactChrome ? '8px' : '12px', flex: 1, minWidth: 0 }}>
              <button
                onClick={async () => {
                  if (locked && !readingMode) return;
                  if (readingMode) {
                    handleReadingAction(isRead ? "unread" : "read");
                    return;
                  }
                  if (!onToggleRead) return;
                  await runFeedbackFlow({
                    actionId: `toggle-read:${textId}`,
                    label: isRead ? "No leído" : "Leído",
                    dbStep: async () => {
                      await onToggleRead(deskId, drawerId, notebookId, textId);
                    },
                    successMessage: "OK",
                    errorMessage: "No se pudo cambiar leído",
                  }).catch((err) => {
                    console.error("[TextReader] toggleRead failed:", err);
                  });
                }}
                style={{ 
                  ...styles.readStatusBtn, 
                  ...(compactChrome ? { padding: "8px", flexShrink: 0 } : {}),
                  color: isRead ? "#4ADE80" : "#D4AF37",
                  borderColor: isRead ? "rgba(74, 222, 128, 0.4)" : "rgba(212, 175, 55, 0.4)",
                  ...(locked && !readingMode ? { opacity: 0.35, cursor: "not-allowed" } : {})
                }}
                title={isRead ? "Leído" : "Marcar como leído"}
                aria-label={isRead ? "Leído" : "Marcar como leído"}
                disabled={!readingMode && (locked || !onToggleRead)}
              >
                {isRead ? <BookMarked size={18} /> : <BookOpen size={18} />}
                {!compactChrome && (
                  <span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{isRead ? "LEÍDO" : "POR LEER"}</span>
                )}
              </button>
              <h2 style={{ ...styles.title, ...(compactChrome ? { fontSize: "1.15rem", lineHeight: 1.25 } : {}) }}>{displayTitle}</h2>
              {readingMode && <span className="reading-mode-badge">Solo lectura</span>}
            </div>
            
            {compactChrome ? (
              <div style={{ position: "relative", flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setMobileToolsOpen((o) => !o)}
                  style={styles.copyBtn}
                  aria-label="Más opciones"
                  aria-expanded={mobileToolsOpen}
                >
                  <MoreVertical size={18} />
                </button>
                {mobileToolsOpen && (
                  <div style={styles.mobileToolsMenu}>
                    <button type="button" style={styles.mobileToolItem} onClick={() => { setIsFocusMode(!isFocusMode); setMobileToolsOpen(false); }}>
                      {isFocusMode ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                      Enfoque
                    </button>
                    <button type="button" style={styles.mobileToolItem} onClick={() => { setIsTerminalMode(!isTerminalMode); setMobileToolsOpen(false); }}>
                      <Terminal size={16} />
                      Terminal
                    </button>
                    {!isMobile && (
                      <button type="button" style={styles.mobileToolItem} onClick={() => { setIsStudyMode(!isStudyMode); setMobileToolsOpen(false); }}>
                        <Sidebar size={16} />
                        Estudio
                      </button>
                    )}
                    <button type="button" style={styles.mobileToolItem} onClick={() => { handleCopyUrl(); setMobileToolsOpen(false); }}>
                      {copied ? <Check size={16} /> : <LinkIcon size={16} />}
                      Copiar enlace
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button
                  onClick={() => setIsFocusMode(!isFocusMode)}
                  style={{ ...styles.copyBtn, color: isFocusMode ? "#D4AF37" : "#aaa" }}
                  title={isFocusMode ? "Modo Split" : "Modo Enfoque"}
                >
                  {isFocusMode ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button
                  onClick={() => setIsTerminalMode(!isTerminalMode)}
                  style={{ ...styles.copyBtn, color: isTerminalMode ? "#4ADE80" : "#aaa" }}
                  title={isTerminalMode ? "Modo Terminal (Monospace)" : "Modo Texto"}
                >
                  <Terminal size={16} />
                </button>
                <button
                  onClick={() => setIsStudyMode(!isStudyMode)}
                  style={{ ...styles.copyBtn, color: isStudyMode ? "#D4AF37" : "#aaa" }}
                  title={isStudyMode ? "Modo Lectura" : "Modo Estudio (Split)"}
                >
                  {isStudyMode ? <Minimize2 size={16} /> : <Sidebar size={16} />}
                </button>
                <button
                  onClick={handleCopyUrl}
                  style={{ ...styles.copyBtn, color: copied ? "#4ADE80" : "#D4AF37" }}
                >
                  {copied ? <Check size={16} /> : <LinkIcon size={16} />}
                </button>
              </div>
            )}
          </div>
          
          {!compactChrome && (
            <div style={styles.metaRow}>
              {formattedDate && <span style={styles.meta}>Última edición: {formattedDate}</span>}
              <div style={styles.langs}>
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    style={{
                      ...styles.langBtn,
                      background: lang === l.code ? "rgba(212,175,55,0.25)" : "rgba(255,255,255,0.05)",
                      color: lang === l.code ? "#D4AF37" : "#aaa",
                    }}
                    onClick={() => handleLangChange(l.code)}
                    disabled={loading}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {compactChrome && (
            <div style={{ ...styles.metaRow, marginTop: "8px", flexWrap: "wrap", gap: "8px" }}>
              {formattedDate && <span style={{ ...styles.meta, fontSize: "0.75rem" }}>{formattedDate}</span>}
              <div style={styles.langs}>
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    style={{
                      ...styles.langBtn,
                      padding: "4px 10px",
                      background: lang === l.code ? "rgba(212,175,55,0.25)" : "rgba(255,255,255,0.05)",
                      color: lang === l.code ? "#D4AF37" : "#aaa",
                    }}
                    onClick={() => handleLangChange(l.code)}
                    disabled={loading}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!locked && quickMoveLocation && (
            <QuickMoveBar
              compact
              location={quickMoveLocation}
              settings={settings}
              onMoved={(receipt) => setMoveLocation(receipt)}
            />
          )}
        </div>

        {/* Body (Split or Single) */}
        <div style={{ ...styles.readerBody, ...(readingLayout ? styles.readerBodyContained : {}) }}>
          {/* Main Text Pane */}
          <div
            ref={scrollPaneRef}
            {...(readingMode ? readerSwipe : {})}
            style={{
            ...styles.readerScrollPane,
            ...(readingLayout ? styles.readerScrollPaneContained : {}),
            flex: isStudyMode && !isMobile ? `0 0 ${splitWidth}%` : '1',
            padding: isStudyMode && !isMobile ? '40px' : (compactChrome ? '0 4px' : '0'),
            borderRight: isStudyMode && !isMobile ? '1px solid rgba(212,175,55,0.1)' : 'none'
          }}>
            <TranslationOverlay active={loading} targetLang={lang} />
            {img && !isStudyMode && (
              <img 
                src={img} 
                alt={displayTitle} 
                style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(212,175,55,0.2)' }} 
              />
            )}
            <div style={{ 
              ...styles.content, 
              fontFamily: isTerminalMode ? "'Fira Code', 'JetBrains Mono', monospace" : "'Playfair Display', serif",
              fontSize: isTerminalMode ? "0.95rem" : (compactChrome ? "1.05rem" : "1.2rem"),
              whiteSpace: isTerminalMode ? "pre-wrap" : "normal",
              lineHeight: isTerminalMode ? "1.4" : "1.75",
              padding: compactChrome ? "12px 4px 24px" : undefined,
            }}>
              <MarkdownRenderer 
                content={displayContent}
                className="markdown-body--reading"
                onCheckboxToggle={readingMode || locked ? undefined : handleCheckboxToggle}
              />
            </div>
            {kind === "folder" && (
              <div style={{ marginTop: 28, paddingTop: 16, borderTop: "1px solid rgba(212,175,55,0.15)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <BookMarked size={16} color="#D4AF37" />
                  <h3 style={{ margin: 0, fontSize: "0.85rem", color: "#D4AF37", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    Checklist
                    {folderProg.total > 0 && (
                      <span style={{ marginLeft: 8, opacity: 0.6, fontWeight: 400 }}>
                        {folderProg.done}/{folderProg.total}
                      </span>
                    )}
                  </h3>
                </div>
                <form
                  onSubmit={handleAddChecklistItem}
                  style={{ display: "flex", gap: 8, marginBottom: 12 }}
                >
                  <input
                    value={checklistDraft}
                    onChange={(e) => setChecklistDraft(e.target.value)}
                    disabled={locked || addingChecklist}
                    placeholder="Nuevo ítem…"
                    aria-label="Nuevo ítem de checklist"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: 42,
                      padding: "0 12px",
                      borderRadius: 8,
                      border: "1px solid rgba(212,175,55,0.25)",
                      background: "rgba(0,0,0,0.18)",
                      color: "#fff",
                      outline: "none",
                    }}
                  />
                  <button
                    type="submit"
                    disabled={locked || addingChecklist || !checklistDraft.trim()}
                    style={{
                      minWidth: 44,
                      minHeight: 42,
                      borderRadius: 8,
                      border: "1px solid rgba(212,175,55,0.45)",
                      background: "rgba(212,175,55,0.16)",
                      color: "#D4AF37",
                      cursor: "pointer",
                      fontSize: "1.2rem",
                    }}
                    aria-label="Agregar ítem"
                  >
                    {addingChecklist ? "…" : "+"}
                  </button>
                </form>
                {folderChildren.length === 0 ? (
                  <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.9rem" }}>Sin ítems todavía. Agregá el primero arriba.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {folderChildren.map((child) => (
                      <div
                        key={child.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 10px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <button
                          type="button"
                          disabled={locked}
                          onClick={(e) => handleChildDone(child, e)}
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            border: child.done ? "none" : "2px solid rgba(255,255,255,0.35)",
                            background: child.done ? "#4ade80" : "transparent",
                            cursor: locked ? "not-allowed" : "pointer",
                            flexShrink: 0,
                          }}
                        >
                          {child.done && <span style={{ color: "#000", fontSize: 11, fontWeight: "bold" }}>✓</span>}
                        </button>
                        <button
                          type="button"
                          onClick={() => onOpenChild?.(child)}
                          disabled={!onOpenChild}
                          style={{
                            flex: 1,
                            textAlign: "left",
                            background: "none",
                            border: "none",
                            color: child.done ? "rgba(255,255,255,0.4)" : "#fff",
                            fontSize: "0.95rem",
                            textDecoration: child.done ? "line-through" : "none",
                            cursor: onOpenChild ? "pointer" : "default",
                            padding: 0,
                          }}
                        >
                          {child.kind === "folder" ? "📁 " : ""}
                          {child.title || "Sin título"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Resizer Handle */}
          {isStudyMode && (
            <div 
              onMouseDown={startResizing}
              style={{
                width: '8px',
                cursor: 'col-resize',
                backgroundColor: 'rgba(212, 175, 55, 0.05)',
                borderLeft: '1px solid rgba(212,175,55,0.1)',
                borderRight: '1px solid rgba(212,175,55,0.1)',
                zIndex: 10,
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(212, 175, 55, 0.2)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(212, 175, 55, 0.05)'}
            />
          )}

          {/* Marginalia Pane */}
          {isStudyMode && (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              background: 'rgba(0,0,0,0.2)', 
              padding: '30px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <Edit3 size={18} color="#D4AF37" />
                <h3 style={{ margin: 0, fontSize: '1rem', color: '#D4AF37', textTransform: 'uppercase', letterSpacing: '1px' }}>Marginalia / Notas</h3>
              </div>
              
              <textarea 
                placeholder="Escribe tus reflexiones, marginalia o tareas aquí... [ ] para tareas."
                value={marginalia}
                onChange={(e) => setMarginalia(e.target.value)}
                disabled={locked}
                style={styles.marginaliaInput}
              />
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button 
                  onClick={handleSaveMarginalia}
                  disabled={locked || isSavingNote || !marginalia.trim()}
                  style={locked ? { ...styles.btnPrimary, opacity: 0.45, cursor: "not-allowed", filter: "grayscale(1)" } : styles.btnPrimary}
                >
                  {isSavingNote ? "Guardando..." : "Guardar como Nota"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          className={compactChrome || readingLayout ? "reader-actions-mobile" : undefined}
          style={{
            ...styles.actions,
            ...(compactChrome || readingLayout ? styles.actionsMobile : {}),
            ...(readingMode && (compactChrome || readingLayout) ? styles.actionsMobileReading : {}),
          }}
        >
          {readingMode && (
            <ReadingActionBar compact={false} onAction={handleReadingAction} />
          )}
          <div style={compactChrome ? styles.actionsMobileRow : { display: "flex", gap: "12px" }}>
            {!readingMode && (
              <button
                type="button"
                style={{
                  ...styles.btnPrimary,
                  flex: compactChrome ? 1 : undefined,
                  ...(locked ? { opacity: 0.45, cursor: "not-allowed", filter: "grayscale(1)" } : {}),
                }}
                onClick={() => {
                  if (locked) return;
                  onEdit && onEdit();
                }}
                disabled={locked || !onEdit}
              >
                <Edit3 size={18} style={{ marginRight: compactChrome ? "6px" : "8px" }} />
                {isMobile ? "Editar" : "Editar Manuscrito"}
              </button>
            )}
            {readingMode && queueMeta && (
              <button
                type="button"
                style={{ ...styles.btnPrimary, flex: 1 }}
                onClick={() => onReadingAction?.("next")}
              >
                Siguiente {queueMeta.index + 1}/{queueMeta.total}
              </button>
            )}
            <button type="button" style={{ ...styles.btn, flex: compactChrome ? 1 : undefined }} onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

const styles = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.22)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  mobileOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.62)",
    zIndex: 2100,
    display: "flex", flexDirection: "column",
  },
  inlineReadingShell: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  readerBody: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    position: "relative",
    minHeight: 0,
  },
  readerBodyContained: {
    flex: 1,
    minHeight: 0,
  },
  readerScrollPane: {
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
  },
  readerScrollPaneContained: {
    flex: 1,
    minHeight: 0,
    overscrollBehavior: "contain",
  },
  inlineOverlay: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  modal: {
    ...deskGlass.readerPanel,
    width: "100%",
    maxHeight: "90vh",
    position: "relative",
  },
  mobileModal: {
    ...deskGlass.readerPanel,
    borderRadius: "0",
    padding: "12px 12px 0",
    width: "100%",
    border: "none",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    paddingBottom: 0,
  },
  mobileToolsMenu: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: "6px",
    background: "rgba(26, 16, 12, 0.98)",
    border: "1px solid rgba(212,175,55,0.3)",
    borderRadius: "8px",
    padding: "6px",
    zIndex: 50,
    minWidth: "160px",
    boxShadow: "0 12px 32px rgba(0,0,0,0.8)",
  },
  mobileToolItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "10px 12px",
    background: "transparent",
    border: "none",
    borderRadius: "6px",
    color: "#e0e0e0",
    cursor: "pointer",
    fontSize: "0.9rem",
    textAlign: "left",
  },
  inlineModal: {
    background: "transparent",
    padding: "32px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    position: "relative",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  header: { borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "20px" },
  title: { ...deskGlass.readerTitle },
  readStatusBtn: { display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", borderRadius: "20px", border: "1px solid", background: "rgba(0,0,0,0.2)", cursor: "pointer", transition: "all 0.2s" },
  copyBtn: { display: "flex", alignItems: "center", gap: "6px", padding: "8px", borderRadius: "6px", border: "1px solid rgba(212,175,55,0.2)", background: "rgba(0,0,0,0.2)", cursor: "pointer" },
  metaRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px" },
  meta: { color: "#886B4A", fontSize: "0.85rem", fontStyle: "italic" },
  langs: { display: "flex", gap: "8px" },
  langBtn: { padding: "6px 12px", borderRadius: "6px", border: "1px solid rgba(212,175,55,0.2)", background: "rgba(0,0,0,0.3)", color: "#aaa", cursor: "pointer", fontSize: "0.8rem", fontWeight: "bold" },
  content: { color: "#F9F6EE", fontSize: "1.2rem", lineHeight: "1.8", fontFamily: "'Playfair Display', serif", textShadow: "0 2px 4px rgba(0,0,0,0.5)" },
  marginaliaInput: { flex: 1, background: "rgba(26, 16, 12, 0.4)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: "8px", padding: "20px", color: "#ccc", fontSize: "1rem", fontFamily: "inherit", resize: "none", outline: "none", lineHeight: "1.6" },
  actions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    borderTop: "1px solid rgba(212,175,55,0.12)",
    paddingTop: "20px",
    padding: "0 32px 32px",
    flexShrink: 0,
  },
  actionsMobile: {
    flexShrink: 0,
    flexDirection: "column",
    alignItems: "stretch",
    gap: "10px",
    marginTop: "auto",
    padding: "10px 10px max(14px, env(safe-area-inset-bottom))",
    background: "rgba(18, 12, 10, 0.98)",
    borderTop: "1px solid rgba(212,175,55,0.28)",
    position: "sticky",
    bottom: 0,
    zIndex: 100,
    pointerEvents: "auto",
    isolation: "isolate",
    boxShadow: "0 -8px 24px rgba(0,0,0,0.45)",
  },
  actionsMobileReading: {
    paddingTop: "12px",
  },
  actionsMobileRow: {
    display: "flex",
    gap: "8px",
    width: "100%",
  },
  btn: { ...deskGlass.btnSecondary },
  btnPrimary: { ...deskGlass.btnPrimary },
};

